import { Search, Bell, ShoppingCart, MessageCircle, User, LogOut, Sparkles, Settings, Menu, Lightbulb, Package, GraduationCap, GitCompare, Heart, Crown, ChevronDown, LayoutDashboard, FileText } from "lucide-react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { useState, useEffect, FormEvent } from "react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Separator } from "@/components/ui/separator";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import circleNetworkLogo from "@/assets/circle-network-logo.png";
import { useAuth } from "@/contexts/AuthContext";
import { membershipService } from "@/data/membership";
import { EditModeToggle } from "@/components/admin/EditModeToggle";
import { ProAvatar } from "@/components/ui/ProAvatar";
import { isFeatureEnabled } from "@/lib/featureFlags";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";

export const Header = ({ forceVisible }: { forceVisible?: boolean } = {}) => {
  const { user, isEditor, isProMember, isAdmin, isVendor, membership, avatarUrl, signOut } = useAuth();
  const isPro = membershipService.isPro(membership);
  const navigate = useNavigate();
  const location = useLocation();
  const isFounder = membershipService.isFounder(membership);
  const [searchQuery, setSearchQuery] = useState('');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [isFirstVisit, setIsFirstVisit] = useState(false);
  
  // Only hide header on product detail pages
  const shouldHideOnScroll = location.pathname.startsWith('/product/');

  // Check if this is the user's first visit
  useEffect(() => {
    const hasVisited = localStorage.getItem('hasVisitedBefore');
    setIsFirstVisit(!hasVisited);
  }, []);

  // Use forceVisible prop if provided, otherwise use internal scroll logic
  const shouldBeVisible = forceVisible !== undefined ? forceVisible : isVisible;

  useEffect(() => {
    // Only run internal scroll handler if not controlled externally and on product pages
    if (forceVisible !== undefined || !shouldHideOnScroll) return;
    
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      
      // Only show header when at the very top
      if (currentScrollY < 50) {
        setIsVisible(true);
      } else {
        setIsVisible(false);
      }
      
      setLastScrollY(currentScrollY);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [lastScrollY, forceVisible, shouldHideOnScroll]);

  const handleSignOut = async () => {
    await signOut();
    navigate('/');
  };

  const handleSearch = (e: FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/browse?query=${encodeURIComponent(searchQuery.trim())}`);
      setSearchQuery('');
      setMobileMenuOpen(false);
    }
  };

  const handleNavigation = (path: string) => {
    navigate(path);
    setMobileMenuOpen(false);
  };

  return <header className={`sticky top-0 z-50 w-full border-b bg-background transition-all duration-300 ease-in-out ${shouldBeVisible ? 'translate-y-0' : '-translate-y-full'}`}>
      {/* Beta Banner */}
      <div className="bg-gradient-to-r from-orange-600 via-amber-600 to-orange-600 text-white py-2 px-4 text-center text-xs sm:text-sm font-medium">
        🚧 Beta: Live Q1 2026 — site is under construction 🚧
      </div>
      
      <div className="px-4 lg:px-6 xl:px-8 pt-3">
        {/* First Row: Logo, Search, Actions */}
        <div className="flex h-12 items-center justify-between gap-4">
          {/* Mobile: Hamburger - Logo (centered) - Actions */}
          <div className="flex items-center gap-3 md:hidden">
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => setMobileMenuOpen(true)}
              aria-label="Open menu"
            >
              <Menu className="h-5 w-5" />
            </Button>
          </div>

          {/* Desktop: Logo - Search - Actions */}
          <Link to={user ? "/dashboard" : "/"} className="hidden md:flex items-center gap-3">
            <img src={circleNetworkLogo} alt="Circle Network" className="h-12 w-12 md:h-14 md:w-14" width={144} height={144} fetchPriority="high" />
            <span className="text-xl md:text-2xl font-bold tracking-tight whitespace-nowrap">MARKETPLACE</span>
          </Link>

          {/* Mobile: Centered Logo */}
          <Link to={user ? "/dashboard" : "/"} className="md:hidden absolute left-1/2 -translate-x-1/2 flex items-center gap-2">
            <img src={circleNetworkLogo} alt="Circle Network" className="h-9 w-9" width={144} height={144} fetchPriority="high" />
            <span className="text-base font-bold tracking-tight">MARKETPLACE</span>
          </Link>

          <div className="hidden md:flex flex-1 max-w-2xl">
            <form onSubmit={handleSearch} className="relative w-full">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input 
                type="search" 
                placeholder="Search products" 
                className="pl-10 w-full h-11 bg-background border-2 shadow-sm focus-visible:shadow-md transition-shadow rounded-full"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </form>
          </div>

          <div className="flex items-center gap-2">
            <Link to="/sell" className="text-sm font-medium hover:text-primary transition-colors hidden lg:inline whitespace-nowrap">
              Sell on Market
            </Link>
            <Button variant="ghost" size="icon">
              <Bell className="h-5 w-5" />
            </Button>
            <Button variant="ghost" size="icon">
              <ShoppingCart className="h-5 w-5" />
            </Button>
            {/* Desktop User Dropdown */}
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild className="hidden md:flex">
                  <Button variant="ghost" className="h-auto p-1.5 hover:bg-accent">
                    <ProAvatar
                      avatarUrl={avatarUrl}
                      email={user.email}
                      isPro={isProMember}
                      isFounder={isFounder}
                      size="md"
                    />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56 bg-white">
                  <DropdownMenuLabel className="flex items-center justify-between">
                    <span>My Account</span>
                    {isFounder && (
                      <Badge variant="secondary" className="bg-gradient-to-r from-amber-500 to-orange-500 text-white text-xs">
                        <Sparkles className="h-3 w-3 mr-1" />
                        Founder
                      </Badge>
                    )}
                    {isProMember && !isFounder && (
                      <Badge variant="default" className="text-xs">Pro</Badge>
                    )}
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate('/account/profile')}>
                    Profile
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/account/favorites')}>
                    Favorites
                  </DropdownMenuItem>
                  {isAdmin && (
                    <DropdownMenuItem onClick={() => navigate('/account/products')}>
                      Products
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem onClick={() => navigate('/account/payments')}>
                    Payments & billing
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/account/credits')}>
                    Credits & gift cards
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/account/rewards')}>
                    Rewards
                  </DropdownMenuItem>
                  {(!isProMember && !isFounder) && (
                    <DropdownMenuItem onClick={() => navigate('/account/pro')}>
                      <div className="flex items-center justify-between w-full">
                        <span>Upgrade to Pro</span>
                        <Badge variant="secondary" className="bg-orange-500 text-white text-xs ml-2">PRO</Badge>
                      </div>
                    </DropdownMenuItem>
                  )}
                  {isVendor && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={() => navigate('/vendor')}>
                        <LayoutDashboard className="h-4 w-4 mr-2" />
                        Vendor Dashboard
                      </DropdownMenuItem>
                    </>
                  )}
                  {isAdmin && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={() => navigate('/admin')}>
                        <Settings className="h-4 w-4 mr-2" />
                        Admin Dashboard
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate('/admin/vendor-updates')}>
                        <Package className="h-4 w-4 mr-2" />
                        Vendor Updates
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate('/admin/change-requests')}>
                        <Package className="h-4 w-4 mr-2" />
                        Change Requests
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate('/admin/review-moderation')}>
                        <FileText className="h-4 w-4 mr-2" />
                        Review Moderation
                      </DropdownMenuItem>
                    </>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleSignOut} className="text-destructive">
                    <LogOut className="h-4 w-4 mr-2" />
                    Log out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Link 
                to={isFirstVisit ? "/pricing" : "/auth"} 
                className="hidden md:inline"
                onClick={() => {
                  if (isFirstVisit) {
                    localStorage.setItem('hasVisitedBefore', 'true');
                    setIsFirstVisit(false);
                  }
                }}
              >
                <Button variant="default" size="sm" className="font-semibold">
                  <User className="h-4 w-4 mr-1" />
                  {isFirstVisit ? "Start for Free" : "Sign In"}
                </Button>
              </Link>
            )}
          </div>
        </div>

        {/* Mobile Search Bar - Always visible below header */}
        <div className="md:hidden py-2">
          <form onSubmit={handleSearch} className="relative w-full">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input 
              type="search" 
              placeholder="Search products" 
              className="pl-10 w-full h-9 bg-muted/30 border-2 rounded-full"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </form>
        </div>

        {/* Second Row: Navigation Links */}
        <nav className="hidden md:flex items-center gap-6 pb-2 pt-2">
          {user ? (
            <Link to="/dashboard" className="text-sm font-medium hover:text-blue-theme transition-colors">Dashboard</Link>
          ) : (
            <Link to="/" className="text-sm font-medium hover:text-blue-theme transition-colors">Home</Link>
          )}
          <Link to="/browse" className="text-sm font-medium hover:text-blue-theme transition-colors">Agent Services</Link>
          <Link to="/browse?type=education" className="text-sm font-medium hover:text-blue-theme transition-colors">Courses & More</Link>
          {isFeatureEnabled('bundles') && (
            <Link to="/gearbundles" className="text-sm font-medium hover:text-blue-theme transition-colors">Agent Tools & Gear</Link>
          )}
          {isFeatureEnabled('bestOfPages') && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="text-sm font-medium hover:text-blue-theme transition-colors flex items-center gap-1">
                  Best Of
                  <ChevronDown className="h-3 w-3" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="bg-white">
                <DropdownMenuItem asChild>
                  <Link to="/best/crm">Best CRM</Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/best/lead-generation">Best Lead Generation</Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/best/transaction-management">Best Transaction Management</Link>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          <Link to="/compare" className="text-sm font-medium hover:text-blue-theme transition-colors">Compare tools</Link>
          <div className="ml-auto flex items-center gap-4">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Link to="/ask-circle">
                    <Button variant="secondary" size="sm" className="bg-emerald-100 text-emerald-800 hover:bg-emerald-200 font-semibold gap-2">
                      <MessageCircle className="h-4 w-4" />
                      <span className="flex items-baseline">
                        <span>Ask</span>
                        <span className="font-handelson font-extralight text-xl -ml-0.5">Circle</span>
                      </span>
                    </Button>
                  </Link>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Not sure what to buy? AskCircle compares tools, reviews, and ROI instantly.</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <EditModeToggle />
          </div>
        </nav>

      </div>

      {/* Mobile Drawer Menu */}
      <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
        <SheetContent side="left" className="w-80 p-0 flex flex-col">
          {/* Fixed Header */}
          <div className="px-6 py-4 border-b">
            <SheetTitle className="text-left">Menu</SheetTitle>
          </div>

          {/* Scrollable Content */}
          <div className="flex-1 overflow-y-auto px-6 pb-6">
            <div className="mt-4 space-y-5">
              
              {/* Sign In CTA - Show at TOP if not logged in */}
              {!user && (
                <>
                  <Link 
                    to={isFirstVisit ? "/pricing" : "/auth"} 
                    onClick={() => {
                      setMobileMenuOpen(false);
                      if (isFirstVisit) {
                        localStorage.setItem('hasVisitedBefore', 'true');
                        setIsFirstVisit(false);
                      }
                    }}
                  >
                    <Button variant="default" size="lg" className="w-full font-semibold shadow-lg">
                      <User className="h-5 w-5 mr-2" />
                      {isFirstVisit ? "Start for Free" : "Sign In / Create Account"}
                    </Button>
                  </Link>
                  <Separator />
                </>
              )}

              {/* User Profile Section - Show at top if logged in */}
              {user && (
                <>
                  <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
                    <ProAvatar
                      avatarUrl={avatarUrl}
                      email={user.email}
                      isPro={isProMember}
                      isFounder={isFounder}
                      size="lg"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{user.email}</p>
                      {isFounder && (
                        <Badge variant="secondary" className="bg-gradient-to-r from-amber-500 to-orange-500 text-white text-xs mt-1">
                          <Sparkles className="h-3 w-3 mr-1" />
                          Founder
                        </Badge>
                      )}
                      {isProMember && !isFounder && (
                        <Badge variant="default" className="text-xs mt-1">Pro</Badge>
                      )}
                    </div>
                  </div>
                  <Separator />
                </>
              )}

              {/* Navigation Categories */}
              <div className="space-y-2">
                <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">Browse</h3>
                {user ? (
                  <button
                    onClick={() => handleNavigation('/dashboard')}
                    className="flex items-center gap-3 w-full p-3 hover:bg-muted rounded-lg transition-colors text-left"
                  >
                    <LayoutDashboard className="h-5 w-5 text-primary" />
                    <span className="font-medium">Dashboard</span>
                  </button>
                ) : (
                  <button
                    onClick={() => handleNavigation('/')}
                    className="flex items-center gap-3 w-full p-3 hover:bg-muted rounded-lg transition-colors text-left"
                  >
                    <LayoutDashboard className="h-5 w-5 text-primary" />
                    <span className="font-medium">Home</span>
                  </button>
                )}
                <button
                  onClick={() => handleNavigation('/browse?category=software')}
                  className="flex items-center gap-3 w-full p-3 hover:bg-muted rounded-lg transition-colors text-left"
                >
                  <Package className="h-5 w-5 text-primary" />
                  <span className="font-medium">Agent Tools</span>
                </button>
                <button
                  onClick={() => handleNavigation('/browse?type=education')}
                  className="flex items-center gap-3 w-full p-3 hover:bg-muted rounded-lg transition-colors text-left"
                >
                  <GraduationCap className="h-5 w-5 text-primary" />
                  <span className="font-medium">Courses & More</span>
                </button>
                {isFeatureEnabled('bundles') && (
                  <button
                    onClick={() => handleNavigation('/gearbundles')}
                    className="flex items-center gap-3 w-full p-3 hover:bg-muted rounded-lg transition-colors text-left"
                  >
                    <Package className="h-5 w-5 text-primary" />
                    <span className="font-medium">Agent Tools & Gear</span>
                  </button>
                )}
                <button
                  onClick={() => handleNavigation('/compare')}
                  className="flex items-center gap-3 w-full p-3 hover:bg-muted rounded-lg transition-colors text-left"
                >
                  <GitCompare className="h-5 w-5 text-primary" />
                  <span className="font-medium">Comparison</span>
                </button>
              </div>

              <Separator />

              {/* Pro Membership Banner (if not Pro) */}
              {!isProMember && !isFounder && (
                <>
                  <button
                    onClick={() => handleNavigation('/account/pro')}
                    className="w-full p-4 bg-gradient-to-r from-orange-500 to-amber-500 text-white rounded-lg hover:opacity-90 transition-opacity text-left"
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-bold text-sm">MARKETPLACE PLUS</p>
                        <p className="text-xs mt-1">Save 10% on everything</p>
                      </div>
                      <Badge variant="secondary" className="bg-white text-orange-600 font-bold">PRO</Badge>
                    </div>
                  </button>
                  <Separator />
                </>
              )}

              {/* Sell on Market Promo */}
              <button
                onClick={() => handleNavigation('/sell')}
                className="w-full p-4 bg-emerald-50 border-2 border-emerald-200 rounded-lg hover:bg-emerald-100 transition-colors text-left"
              >
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-emerald-500 rounded-lg">
                    <Lightbulb className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <p className="font-semibold text-emerald-900">Sell on Marketplace</p>
                    <p className="text-xs text-emerald-700 mt-1">Earn money selling digital products</p>
                  </div>
                </div>
              </button>

              <Separator />

              {/* Account Links - Only for logged-in users */}
              {user && (
                <div className="space-y-1">
                  <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">Account</h3>
                  <button
                    onClick={() => handleNavigation('/account/profile')}
                    className="flex items-center gap-3 w-full p-3 hover:bg-muted rounded-lg transition-colors text-left"
                  >
                    <User className="h-4 w-4" />
                    <span>Profile</span>
                  </button>
                  <button
                    onClick={() => handleNavigation('/account/favorites')}
                    className="flex items-center gap-3 w-full p-3 hover:bg-muted rounded-lg transition-colors text-left"
                  >
                    <Heart className="h-4 w-4" />
                    <span>Favorites</span>
                  </button>
                  {isAdmin && (
                    <button
                      onClick={() => handleNavigation('/account/products')}
                      className="flex items-center gap-3 w-full p-3 hover:bg-muted rounded-lg transition-colors text-left"
                    >
                      <Package className="h-4 w-4" />
                      <span>Products</span>
                    </button>
                  )}
                  <button
                    onClick={() => handleNavigation('/account/payments')}
                    className="flex items-center gap-3 w-full p-3 hover:bg-muted rounded-lg transition-colors text-left"
                  >
                    <span>Payments & billing</span>
                  </button>
                  <button
                    onClick={() => handleNavigation('/account/credits')}
                    className="flex items-center gap-3 w-full p-3 hover:bg-muted rounded-lg transition-colors text-left"
                  >
                    <span>Credits & gift cards</span>
                  </button>
                  <button
                    onClick={() => handleNavigation('/account/rewards')}
                    className="flex items-center gap-3 w-full p-3 hover:bg-muted rounded-lg transition-colors text-left"
                  >
                    <span>Rewards</span>
                  </button>
                  {(!isProMember && !isFounder) && (
                    <button
                      onClick={() => handleNavigation('/account/pro')}
                      className="flex items-center justify-between w-full p-3 hover:bg-muted rounded-lg transition-colors text-left"
                    >
                      <span>Get 10% off</span>
                      <Badge variant="secondary" className="bg-orange-500 text-white text-xs">PRO</Badge>
                    </button>
                  )}
                  {isAdmin && (
                    <button
                      onClick={() => handleNavigation('/admin')}
                      className="flex items-center gap-3 w-full p-3 hover:bg-muted rounded-lg transition-colors text-left"
                    >
                      <Settings className="h-4 w-4" />
                      <span>Admin Dashboard</span>
                    </button>
                  )}
                  <Separator className="my-3" />
                  <button
                    onClick={handleSignOut}
                    className="flex items-center gap-3 w-full p-3 hover:bg-destructive/10 text-destructive rounded-lg transition-colors text-left"
                  >
                    <LogOut className="h-4 w-4" />
                    <span>Log out</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </SheetContent>
      </Sheet>
    </header>;
};